import React from 'react';
import { BaseObject, UseStandardTableProps, StandardTableInstance, UseNewTableProps } from '../../../types/Tables';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { PaginatedResult } from '../../../types/PaginatedResult';
import { parseError } from '../../../utils';
import { resolvePageCount } from './components/resolvePageCount';

export function useStandardTable<T extends object = BaseObject>({
  columns,
  fetchAsync,
  exportAsync,
  resolveData: overridedResolveData,
  params,
  accessKey,
  onRowClick,
  onFetchComplete,
  serverSide = false,
  shouldFetch = true,
  defaultColumn: overridedDefaultColumn,
  ...rest
}: UseStandardTableProps<T>): StandardTableInstance<T> {
  const fetchIdRef = React.useRef(0);
  const [data, setData] = React.useState<T[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [totalRows, setTotalRows] = React.useState(0);
  const [lastUpdated] = React.useState<any>();
  const [pageCount, setPageCount] = React.useState(0);
  const { notifyMessage } = useNotifyMessage();
  const showFooter =
    columns.findIndex((column) => column.footer !== undefined) > -1 || Boolean(overridedDefaultColumn && overridedDefaultColumn.footer !== undefined); // Get rid of the empty footer

  const resolveData = React.useCallback(
    (response: any) => {
      if (overridedResolveData) return overridedResolveData(response);
      return response.data;
    },
    [overridedResolveData],
  );

  const canExportData = (): boolean => (exportAsync ? true : false);

  const exportData = React.useCallback(
    async ({ sortBy }: any) => {
      if (exportAsync) {
        setIsExporting(true);
        try {
          await exportAsync({
            ...params,
            ...(sortBy &&
              sortBy.length > 0 && {
                sortBy: [
                  {
                    [sortBy[0].id]: sortBy[0].desc ? '-1' : '1',
                  },
                ],
              }),
          });
        } catch (e: any) {
          console.error('Unable to export data', e);
          notifyMessage(parseError(e), 'error');
        }
      }
      setIsExporting(false);
    },
    [fetchAsync, resolveData, params, shouldFetch],
  );

  const fetchData = React.useCallback(
    async ({ pageIndex: page, pageSize: size, sortBy, manualPageCount }: any) => {
      const fetchId = ++fetchIdRef.current; // bump ID for this call
      setLoading(true);

      if (shouldFetch && fetchAsync) {
        try {
          const res = await fetchAsync({
            ...params,
            // page,
            // size,
            skip: page && size ? page * size : 0,
            take: size ?? 100,
            ...(sortBy &&
              sortBy.length > 0 && {
                sortBy: [
                  {
                    [sortBy[0].id]: sortBy[0].desc ? '-1' : '1',
                  },
                ],
              }),
            //fq: fields || '',
            // q: words || '',
          });

          // Prevent error
          if (res?.status === 200) {
            const { results, totalCount } = resolveData(res);

            // Check if `results` and `totalCount` exist, otherwise assume res.data is the result array
            const dataToSet = results ?? (Array.isArray(res.data) ? res.data : [res.data]) ?? [];
            const totalRowsToSet = totalCount ?? dataToSet.length;
            setData(dataToSet);
            setTotalRows(totalRowsToSet);

            // Feeds react-table's internal model only. Next/Previous for
            // server-side tables is decided in Table.tsx from the row count.
            setPageCount(resolvePageCount(dataToSet.length, size, page, totalCount));

            if (onFetchComplete) onFetchComplete(res.data);
          }
        } catch (e: any) {
          const { message } = e;
          notifyMessage(message, 'error');
        }
      } else {
        //calculate pageCount
        setPageCount(manualPageCount);
      }

      // Set loading to false only if the fetchId matches the current one
      // This prevents setting loading to false if a new fetch is triggered
      // before the previous one completes
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    },
    [fetchAsync, resolveData, params, shouldFetch],
  );

  return {
    ...rest,
    data,
    columns,
    totalRows,
    loading,
    isExporting,
    serverSide,
    params,
    lastUpdated,
    accessKey,
    defaultColumn: overridedDefaultColumn,
    showFooter,
    pageCount,
    exportData,
    canExportData,
    fetchData,
    setData,
    setLoading,
    setIsExporting,
    setPageCount,
    onRowClick,
    getState: () => ({
      data,
      columns,
      totalRows,
      loading,
      isExporting,
      serverSide,
      params,
      lastUpdated,
      accessKey,
      defaultColumn: overridedDefaultColumn,
      showFooter,
      pageCount,
    }),
  };
}

/**
 * This is the new typesafe table with typesafe fetch methods and proper error handling.
 *
 * **Migrating from standardTable to newTable**:
 *
 * - Ensure `fetchAsync` returns a `Promise<PaginatedResult<T>>`
 *   rather than a `Promise<SingleResult<PaginatedResult<T>>>`.
 * - You can do this by updating a service to use the `httpGet_New<PaginatedResult<T>>` method
 *   instead of the old `httpGet<SingleResult<PaginatedResult<T>>`.
 */
export function useNewTable<T extends object = BaseObject>({
  columns,
  fetchAsync,
  exportAsync,
  resolveData,
  params,
  accessKey,
  onRowClick,
  onFetchComplete,
  serverSide = false,
  shouldFetch = true,
  defaultColumn: overridedDefaultColumn,
  ...rest
}: UseNewTableProps<T>): StandardTableInstance<T> {
  const fetchIdRef = React.useRef(0);
  const [data, setData] = React.useState<T[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [totalRows, setTotalRows] = React.useState(0);
  const [lastUpdated] = React.useState<any>();
  const [pageCount, setPageCount] = React.useState(0);
  const { notifyMessage } = useNotifyMessage();
  const showFooter =
    columns.findIndex((column) => column.footer !== undefined) > -1 || Boolean(overridedDefaultColumn && overridedDefaultColumn.footer !== undefined); // Get rid of the empty footer

  const handleResolveData = React.useCallback(
    (response: PaginatedResult<T>): PaginatedResult<T> => resolveData?.(response) ?? response,
    [resolveData],
  );

  const canExportData = (): boolean => (exportAsync ? true : false);

  const exportData = React.useCallback(
    async ({ sortBy }: any) => {
      if (exportAsync) {
        setIsExporting(true);
        try {
          await exportAsync({
            ...params,
            ...(sortBy &&
              sortBy.length > 0 && {
                sortBy: [
                  {
                    [sortBy[0].id]: sortBy[0].desc ? '-1' : '1',
                  },
                ],
              }),
          });
        } catch (e: any) {
          console.error('Unable to export data', e);
          notifyMessage(parseError(e), 'error');
        }
      }
      setIsExporting(false);
    },
    [fetchAsync, handleResolveData, params, shouldFetch],
  );

  const fetchData = React.useCallback(
    async ({ pageIndex: page, pageSize: size, sortBy, manualPageCount }: any) => {
      const fetchId = ++fetchIdRef.current; // bump ID for this call
      setLoading(true);

      if (shouldFetch && fetchAsync) {
        try {
          const res: PaginatedResult<T> = await fetchAsync({
            ...params,
            skip: page && size ? page * size : 0,
            take: size ?? 100,
            ...(sortBy &&
              sortBy.length > 0 && {
                sortBy: [
                  {
                    [sortBy[0].id]: sortBy[0].desc ? '-1' : '1',
                  },
                ],
              }),
          });

          const resolvedData = handleResolveData(res);

          const results = Array.isArray(resolvedData?.results) ? resolvedData.results : [];

          // Endpoints whose count query was dropped send totalCount: null — that null has to
          // stay distinct from a real count. Coalescing it to results.length made pageCount 1,
          // and react-table then clamped pageIndex back to 0, so Next never advanced and every
          // request still went out with skip: 0.
          const serverTotalCount = resolvedData?.totalCount ?? null;

          setData(results);
          setTotalRows(serverTotalCount ?? results.length);

          // Feeds react-table's internal model only. Next/Previous for
          // server-side tables is decided in Table.tsx from the row count.
          setPageCount(resolvePageCount(results.length, size, page, serverTotalCount));

          onFetchComplete?.(res);
        } catch (e: any) {
          // Silently ignore aborted/cancelled requests — not a real error
          if (
            e?.name === 'AbortError' ||
            e?.name === 'CanceledError' || // axios cancel
            e?.code === 'ERR_CANCELED' // axios cancel (newer versions)
          ) {
            return;
          }
          console.error('Unable to fetch data:', e);
          notifyMessage(parseError(e), 'error');
        }
      } else {
        //calculate pageCount
        setPageCount(manualPageCount);
      }

      // Set loading to false only if the fetchId matches the current one
      // This prevents setting loading to false if a new fetch is triggered
      // before the previous one completes
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    },
    [fetchAsync, handleResolveData, params, shouldFetch],
  );

  return {
    ...rest,
    data,
    columns,
    totalRows,
    loading,
    isExporting,
    serverSide,
    params,
    lastUpdated,
    accessKey,
    defaultColumn: overridedDefaultColumn,
    showFooter,
    pageCount,
    exportData,
    canExportData,
    fetchData,
    setData,
    setLoading,
    setIsExporting,
    setPageCount,
    onRowClick,
    getState: () => ({
      data,
      columns,
      totalRows,
      loading,
      isExporting,
      serverSide,
      params,
      lastUpdated,
      accessKey,
      defaultColumn: overridedDefaultColumn,
      showFooter,
      pageCount,
    }),
  };
}
